import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
} from "@tanstack/react-table";
import {
  StyledTableWrapper,
  StyledTable,
  StyledTh,
  StyledTd,
  StyledTr,
  TablesContainer,
} from "./CustomDataTable.styles";

interface CustomDataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  loading?: boolean;
}

export const CustomDataTable = <T,>({
  data,
  columns,
  loading = false,
}: CustomDataTableProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (loading) {
    return <div className="text-center text-gray-500">Loading...</div>;
  }

  if (!data.length) {
    return (
      <div className="p-4 text-center text-gray-500">No data available.</div>
    );
  }

  return (
    <TablesContainer>
      <StyledTableWrapper>
        <StyledTable>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <StyledTr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <StyledTh
                    key={header.id}
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    style={{
                      cursor: header.column.getCanSort()
                        ? "pointer"
                        : "default",
                      userSelect: "none",
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}

                    {header.column.getCanSort() && (
                      <span
                        style={{
                          marginLeft: "4px",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        {header.column.getIsSorted() === "asc" && (
                          <svg
                            width="12"
                            height="12"
                            fill="#0f62e7"
                            viewBox="0 0 24 24"
                          >
                            <path d="M7 14l5-5 5 5H7z" /> {/* Up arrow */}
                          </svg>
                        )}
                        {header.column.getIsSorted() === "desc" && (
                          <svg
                            width="12"
                            height="12"
                            fill="#0f62e7"
                            viewBox="0 0 24 24"
                          >
                            <path d="M7 10l5 5 5-5H7z" /> {/* Down arrow */}
                          </svg>
                        )}
                        {!header.column.getIsSorted() && (
                          <svg
                            width="12"
                            height="12"
                            fill="#ccc"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 4 L6 10 H18 Z" />
                            <path d="M12 20 L6 14 H18 Z" />
                          </svg>
                        )}
                      </span>
                    )}
                  </StyledTh>
                ))}
              </StyledTr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <StyledTr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <StyledTd key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </StyledTd>
                ))}
              </StyledTr>
            ))}
          </tbody>
        </StyledTable>
      </StyledTableWrapper>
    </TablesContainer>
  );
};
